import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { HPRESS_LETTER, PERMISSION_GROUPS, PERMISSION_NAMES, type Permission } from '@airpost/shared';
import { ApiError, del, get, post, put } from '../api.js';
import { PageTitle } from '../frame.js';
import { currentLocale, t } from '../i18n.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';
import { Dialog } from './rundown-dialogs.js';

interface RoleDto {
  key: string;
  name: string;
  permissions: Permission[];
  builtIn: boolean;
  userCount: number;
}
interface UserDto {
  id: string;
  username: string;
  displayName: string;
  role: string;
  roleName: string | null;
  active: boolean;
  locale: string;
  hasPassword: boolean;
  isDemo: boolean;
  sourceId: string | null;
  createdAt: string;
  updatedAt: string;
}

const MIN_PASSWORD = 10;
const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

// ── Users ──────────────────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const { session, can } = useSession();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserDto | 'new' | null>(null);

  const load = () =>
    Promise.all([get<{ users: UserDto[] }>('/api/admin/users'), get<{ roles: RoleDto[] }>('/api/roles')])
      .then(([u, r]) => {
        setUsers(u.users);
        setRoles(r.roles);
        setError(null);
      })
      .catch((e) => setError(errText(e)));
  useEffect(() => {
    void load();
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => (showInactive || u.active) && (!needle || u.username.toLowerCase().includes(needle) || u.displayName.toLowerCase().includes(needle) || (u.roleName ?? u.role).toLowerCase().includes(needle)));
  }, [users, q, showInactive]);

  if (!can('admin.users')) return <div className="banner err">{t('noPermission')}</div>;
  return (
    <>
      <PageTitle title={t('usersTitle')} hint={t('usersHint')} />
      <div className="toolbar left">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} style={{ width: 240 }} />
        <label>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> {t('usersShowInactive')}
        </label>
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={() => setEditing('new')}>
          ✚ {t('userNew')}
        </button>
      </div>
      {error && <div className="banner err">{error}</div>}
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('username')}</th>
              <th>{t('displayName')}</th>
              <th>{t('role')}</th>
              <th>{t('status')}</th>
              <th>{t('password')}</th>
              <th>{t('updated')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={7} className="c muted">
                  —
                </td>
              </tr>
            )}
            {shown.map((u) => (
              <tr key={u.id} className={u.active ? '' : 'floated'}>
                <td>
                  {u.username}
                  {u.id === session?.user.id && <span className="status info" style={{ marginLeft: 6 }}>{t('you')}</span>}
                  {u.isDemo && <span className="status warn" style={{ marginLeft: 6 }}>demo</span>}
                </td>
                <td>{u.displayName}</td>
                <td>{u.roleName ?? u.role}</td>
                <td className="c">{u.active ? <span className="status ok">{t('active')}</span> : <span className="status err">{t('inactive')}</span>}</td>
                <td className="c">{u.hasPassword ? '●●●' : <span className="status warn">{t('noPassword')}</span>}</td>
                <td className="c small">{fmtDateTime(u.updatedAt)}</td>
                <td className="actions">
                  <button title={t('edit')} onClick={() => setEditing(u)}>
                    ✎
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tip">{t('usersTip')}</p>
      {editing && (
        <UserDialog
          user={editing === 'new' ? null : editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function UserDialog({ user, roles, onClose, onSaved }: { user: UserDto | null; roles: RoleDto[]; onClose: () => void; onSaved: () => void }) {
  const [username, setUsername] = useState(user?.username ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [role, setRole] = useState(user?.role ?? roles.find((r) => r.key === 'journalist')?.key ?? roles[0]?.key ?? '');
  const [active, setActive] = useState(user?.active ?? true);
  const [locale, setLocale] = useState(user?.locale ?? 'el');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordNeeded = !user;
  const passwordBad = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = repeat !== password;
  const ready = displayName.trim() && role && (!passwordNeeded || password.length >= MIN_PASSWORD) && !passwordBad && !mismatch && (user || /^[A-Za-z0-9._-]{2,40}$/.test(username));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      if (user) {
        const patch: Record<string, unknown> = { displayName: displayName.trim(), role, active, locale };
        if (password) patch.password = password;
        await put(`/api/admin/users/${user.id}`, patch);
      } else {
        await post('/api/admin/users', { username: username.trim(), displayName: displayName.trim(), role, active, locale, password });
      }
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={user ? `${t('userEdit')} — ${user.username}` : t('userNew')} onClose={onClose} width={560}>
      <form onSubmit={submit}>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
          <label htmlFor="u-name">{t('username')} :</label>
          <input id="u-name" value={username} onChange={(e) => setUsername(e.target.value)} disabled={!!user} maxLength={40} autoComplete="off" autoFocus={!user} />
          <label htmlFor="u-disp">{t('displayName')} :</label>
          <input id="u-disp" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={120} autoFocus={!!user} />
          <label htmlFor="u-role">{t('role')} :</label>
          <select id="u-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name} ({r.key})
              </option>
            ))}
          </select>
          <label htmlFor="u-locale">{t('language')} :</label>
          <select id="u-locale" value={locale} onChange={(e) => setLocale(e.target.value)}>
            <option value="el">Ελληνικά</option>
            <option value="en">English</option>
          </select>
          <label htmlFor="u-active">{t('status')} :</label>
          <label>
            <input id="u-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('active')}
          </label>
          <label htmlFor="u-pass">{user ? t('newPasswordOptional') : t('password')} :</label>
          <input id="u-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} maxLength={200} autoComplete="new-password" aria-invalid={passwordBad || undefined} />
          <label htmlFor="u-rep">{t('repeatPassword')} :</label>
          <input id="u-rep" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} maxLength={200} autoComplete="new-password" aria-invalid={(repeat.length > 0 && mismatch) || undefined} />
          <span />
          <div>
            <button className="btn primary" disabled={busy || !ready}>
              💾 {t('save')}
            </button>
            <button type="button" className="btn" style={{ marginLeft: 6 }} onClick={onClose}>
              {t('cancel')}
            </button>
            {passwordBad && (
              <span className="status warn" style={{ marginLeft: 10 }}>
                {t('passwordRule')}
              </span>
            )}
            {!passwordBad && repeat.length > 0 && mismatch && (
              <span className="status warn" style={{ marginLeft: 10 }}>
                {t('passwordMismatch')}
              </span>
            )}
            {error && (
              <span className="status err" style={{ marginLeft: 10 }}>
                {error}
              </span>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  );
}

// ── Roles ──────────────────────────────────────────────────────────────────────

export function AdminRolesPage() {
  const { can } = useSession();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RoleDto | 'new' | null>(null);
  const locale = currentLocale();

  const load = () =>
    get<{ roles: RoleDto[] }>('/api/roles')
      .then((r) => {
        setRoles(r.roles);
        setError(null);
      })
      .catch((e) => setError(errText(e)));
  useEffect(() => {
    void load();
  }, []);

  const remove = async (r: RoleDto) => {
    if (!window.confirm(`${t('roleDeleteConfirm')} ${r.name}?`)) return;
    try {
      await del(`/api/roles/${encodeURIComponent(r.key)}`);
      await load();
    } catch (e) {
      setError(errText(e));
    }
  };

  const letters = (r: RoleDto) =>
    PERMISSION_GROUPS.map((g) => {
      const ls = g.permissions.filter((p) => r.permissions.includes(p)).map((p) => HPRESS_LETTER[p] ?? '•');
      return ls.length ? `${g[locale]}: ${ls.join('')}` : null;
    })
      .filter(Boolean)
      .join(' · ');

  if (!can('admin.users')) return <div className="banner err">{t('noPermission')}</div>;
  return (
    <>
      <PageTitle title={t('rolesTitle')} hint={t('rolesHint')} />
      <div className="toolbar left">
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={() => setEditing('new')}>
          ✚ {t('roleNew')}
        </button>
      </div>
      {error && <div className="banner err">{error}</div>}
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('roleKey')}</th>
              <th>{t('roleName')}</th>
              <th>{t('permissions')}</th>
              <th>{t('users')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.key}>
                <td>
                  <code>{r.key}</code> {r.builtIn && <span className="status info">{t('builtIn')}</span>}
                </td>
                <td>{r.name}</td>
                <td className="small">{r.key === 'admin' ? t('allPermissions') : letters(r) || '—'}</td>
                <td className="num">{r.userCount}</td>
                <td className="actions">
                  <button title={t('edit')} onClick={() => setEditing(r)}>
                    ✎
                  </button>
                  {!r.builtIn && (
                    <button title={t('delete')} disabled={r.userCount > 0} onClick={() => void remove(r)}>
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tip">{t('rolesTip')}</p>
      {editing && (
        <RoleDialog
          role={editing === 'new' ? null : editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function RoleDialog({ role, roles, onClose, onSaved }: { role: RoleDto | null; roles: RoleDto[]; onClose: () => void; onSaved: () => void }) {
  const locale = currentLocale();
  const [key, setKey] = useState(role?.key ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [perms, setPerms] = useState<Set<Permission>>(new Set(role?.permissions ?? []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fixed = role?.key === 'admin';

  const toggle = (p: Permission) =>
    setPerms((s) => {
      const n = new Set(s);
      if (n.has(p)) n.delete(p);
      else n.add(p);
      return n;
    });
  const copyFrom = (k: string) => {
    const src = roles.find((r) => r.key === k);
    if (src) setPerms(new Set(src.permissions));
  };
  const ready = name.trim() && (role || /^[a-z][a-z0-9_-]{1,31}$/.test(key));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      if (role) await put(`/api/roles/${encodeURIComponent(role.key)}`, fixed ? { name: name.trim() } : { name: name.trim(), permissions: [...perms] });
      else await post('/api/roles', { key, name: name.trim(), permissions: [...perms] });
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={role ? `${t('roleEdit')} — ${role.name}` : t('roleNew')} onClose={onClose} width={760}>
      <form onSubmit={submit}>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr max-content 1fr' }}>
          <label htmlFor="r-key">{t('roleKey')} :</label>
          <input id="r-key" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} disabled={!!role} maxLength={32} placeholder="p.x. sports-editor" autoFocus={!role} />
          <label htmlFor="r-name">{t('roleName')} :</label>
          <input id="r-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus={!!role} />
          {!fixed && (
            <>
              <label htmlFor="r-copy">{t('roleCopyFrom')} :</label>
              <select id="r-copy" defaultValue="" onChange={(e) => e.target.value && copyFrom(e.target.value)}>
                <option value="">—</option>
                {roles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        {fixed ? (
          <p className="tip">{t('adminRoleFixed')}</p>
        ) : (
          <div className="perm-groups">
            {PERMISSION_GROUPS.map((g) => (
              <fieldset key={g.key} className="perm-group">
                <legend>{g[locale]}</legend>
                {g.permissions.map((p) => (
                  <label key={p} className="perm">
                    <input type="checkbox" checked={perms.has(p)} onChange={() => toggle(p)} />
                    <span className="perm-letter">{HPRESS_LETTER[p] ?? ''}</span>
                    <span>{PERMISSION_NAMES[p][locale]}</span>
                    <code className="muted">{p}</code>
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
        )}
        <div className="toolbar left" style={{ marginTop: 8 }}>
          <button className="btn primary" disabled={busy || !ready}>
            💾 {t('save')}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          {error && <span className="status err">{error}</span>}
        </div>
      </form>
    </Dialog>
  );
}
